<template>
  <!-- FE-5 · the comparison matrix, lifted out of the Pages page and hung under the GEO site.
       Rows are competitors, columns are the three kinds of comparison page. The empty state, the
       402 and the greyed "Not available yet" column are FE-4's, unchanged — only the container
       moved. -->
  <section :id="anchor" class="cmp-sec" aria-labelledby="cmp-h">
    <div class="cmp-head">
      <h2 id="cmp-h">{{ t("sites_cmp_h") }}</h2>
      <p class="cmp-sub">{{ t("sites_cmp_sub") }}</p>
    </div>

    <p v-if="!currentBrand" class="empty">{{ t("pages_none_brand") }}</p>
    <p v-else-if="comparisons.loading && !matrixRows.length" class="empty" role="status">{{ t("pages_loading") }}</p>
    <p v-else-if="comparisons.on === false" class="empty">{{ t("pages_off_c") }}</p>
    <p v-else-if="comparisons.error" class="empty" role="alert">
      {{ comparisons.error }}
      <button type="button" class="atbtn" @click="reload">{{ t("account_retry") }}</button>
    </p>
    <p v-else-if="!matrixRows.length" class="empty">{{ t("pages_empty_c") }}</p>

    <template v-else>
      <p class="pg-ov" role="status">{{ overviewLine }}</p>

      <div class="pg-matrixwrap">
        <table class="pg-matrix">
          <thead>
            <tr>
              <th scope="col">{{ t("pages_col_competitor") }}</th>
              <th v-for="column in COMPARISON_COLUMNS" :key="column.id" scope="col" :class="{ 'pg-col-off': !column.available }">
                {{ columnLabel(column.id) }}
                <span v-if="!column.available" class="pg-na">{{ t("pages_na") }}</span>
              </th>
              <th scope="col" class="pg-sharecol">{{ t("pages_col_share") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in matrixRows" :key="row.key">
              <th scope="row" class="pg-cname">
                <span class="pg-cn">{{ row.name }}</span>
                <span v-if="row.domain" class="pg-cd">{{ row.domain }}</span>
              </th>
              <td v-for="cell in row.cells" :key="cell.column" class="pg-cell" :class="`pg-is-${cell.state}`">
                <a v-if="cell.canOpen" class="pg-cellbtn" :href="cell.url" target="_blank" rel="noopener" :title="cellTitle(row, cell)">
                  <span class="pg-dot" aria-hidden="true"></span>
                  <span class="pg-celltxt">{{ stateMeta(cell.state).label }}</span>
                  <span v-if="cell.engines.length" class="pg-engines">
                    <span v-for="badge in engineBadges(cell.engines)" :key="badge.id" class="pg-eng" :title="badge.label">{{ badge.short }}</span>
                  </span>
                </a>
                <button
                  v-else-if="cell.canWrite"
                  type="button"
                  class="pg-cellbtn pg-cellnew"
                  :disabled="busy === cellKey(row, cell)"
                  :title="`${t('pages_write_cell')}: ${columnLabel(cell.column)} · ${row.name}`"
                  :aria-label="`${t('pages_write_cell')}: ${columnLabel(cell.column)} · ${row.name}`"
                  @click="writeComparison(row, cell)"
                >{{ busy === cellKey(row, cell) ? "…" : "+" }}</button>
                <span v-else class="pg-cellbtn pg-celloff" :title="cellTitle(row, cell)">
                  <span v-if="cell.state === 'draft'" class="pg-dot" aria-hidden="true"></span>
                  <span class="pg-celltxt">{{ cell.state === "draft" ? stateMeta("draft").label : "—" }}</span>
                </span>
              </td>
              <td class="pg-sharecol">{{ row.share == null ? "—" : `${Math.round(row.share)}%` }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p class="pg-foot">{{ bestForNote }}</p>
      <p v-if="matrixError" class="pg-err" role="alert">{{ matrixError }}</p>
      <ul class="pg-legend">
        <li v-for="state in LEGEND" :key="state" :class="`pg-is-${state}`"><span class="pg-dot" aria-hidden="true"></span>{{ stateMeta(state).label }}</li>
      </ul>
    </template>

    <p class="sr-only" role="status" aria-live="polite">{{ status }}</p>
  </section>
</template>

<script setup>
// The block holds no rules of its own: the cells, the counts and the sentence shown for a refusal
// all come from `api/pages.js`, where they are unit-tested without a browser.

import { computed, onBeforeUnmount, ref, watch } from "vue";

import {
  COMPARISON_COLUMNS,
  comparisonMatrix,
  comparisonOverview,
  engineBadges,
  pagesErrorMessage,
  stateMeta,
  writeComparisonPage,
} from "../api/pages.js";
import { currentBrand } from "../stores/brands";
import { loadComparisons, pagesState } from "../stores/pages.js";
import { useI18n } from "../useI18n";

defineProps({ anchor: { type: String, default: "comparisons" } });

const { t } = useI18n();

const LEGEND = ["none", "draft", "published", "cited"];

const busy = ref("");
const status = ref("");
const matrixError = ref("");
let controller;

const comparisons = computed(() => pagesState.comparisons);
const matrixRows = computed(() =>
  comparisonMatrix(pagesState.comparisons.competitors, pagesState.comparisons.pages, {
    citedPages: pagesState.citedPages,
    share: pagesState.comparisons.share,
  }),
);
const overview = computed(() => comparisonOverview(matrixRows.value));

/** English needs the singular; zh and ja have one form and simply ignore the `_one` key. */
function count(key, n) {
  return t(n === 1 ? `${key}_one` : key, { n });
}

const overviewLine = computed(() => [count("pages_ov_cmp_live", overview.value.live), count("pages_ov_cmp_open", overview.value.open)].join(" · "));
const bestForNote = computed(() => COMPARISON_COLUMNS.find((column) => !column.available)?.hint || "");

function columnLabel(id) {
  return t(`pages_col_${id}`);
}

function cellKey(row, cell) {
  return `${row.key}:${cell.column}`;
}

function cellTitle(row, cell) {
  const column = COMPARISON_COLUMNS.find((entry) => entry.id === cell.column);
  if (cell.state === "unavailable") return column?.hint || "";
  return `${columnLabel(cell.column)} · ${row.name} — ${stateMeta(cell.state).label}`;
}

function reload() {
  controller?.abort();
  controller = new AbortController();
  const brandId = currentBrand.value?.id;
  if (brandId == null) return;
  loadComparisons(brandId, { signal: controller.signal });
}

async function writeComparison(row, cell) {
  busy.value = cellKey(row, cell);
  matrixError.value = "";
  try {
    await writeComparisonPage({
      brandId: currentBrand.value?.id,
      pageType: cell.column,
      competitorIds: [row.competitorId],
    });
    status.value = t("pages_written");
    await loadComparisons(currentBrand.value?.id);
  } catch (error) {
    matrixError.value = pagesErrorMessage(error);
  } finally {
    busy.value = "";
  }
}

watch(() => currentBrand.value?.id, reload, { immediate: true });
onBeforeUnmount(() => controller?.abort());
</script>

<style scoped>
/* Tokens only — every colour resolves in both schemes through app.css's :root. */
.cmp-sec {
  margin-top: 34px;
  padding-top: 26px;
  border-top: 1px solid var(--separator);
  scroll-margin-top: 24px;
}

.cmp-head {
  margin-bottom: 14px;
}

.cmp-head h2 {
  margin: 0;
  font-size: var(--fs-title3);
  letter-spacing: var(--ls-title);
}

.cmp-sub {
  margin: 4px 0 0;
  max-width: 62ch;
  font-size: var(--fs-caption);
  color: var(--mute);
}

.pg-ov {
  margin: 0 0 12px;
  font-size: var(--fs-subhead);
  font-weight: 600;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}

/* ── the state dot ───────────────────────────────────────────────────────────────────────────── */

.pg-dot {
  flex: 0 0 auto;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--mute);
}

.pg-is-none .pg-dot {
  background: transparent;
  box-shadow: inset 0 0 0 1.5px var(--mute);
}

.pg-is-draft .pg-dot {
  background: var(--amber);
}

.pg-is-published .pg-dot {
  background: var(--jade);
}

.pg-is-cited .pg-dot {
  background: var(--indigo);
}

/* ── the matrix ──────────────────────────────────────────────────────────────────────────────── */

.pg-matrixwrap {
  overflow-x: auto;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--card);
}

.pg-matrix {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--fs-caption);
}

.pg-matrix th,
.pg-matrix td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid var(--separator);
  white-space: nowrap;
}

.pg-matrix tbody tr:last-child th,
.pg-matrix tbody tr:last-child td {
  border-bottom: 0;
}

.pg-matrix tbody th,
.pg-matrix thead th:first-child {
  position: sticky;
  left: 0;
  z-index: 1;
  background: var(--card);
}

.pg-matrix thead th {
  font-size: var(--fs-caption2);
  font-weight: 700;
  color: var(--mute);
  vertical-align: bottom;
}

.pg-col-off {
  color: var(--mute);
}

.pg-na {
  display: block;
  margin-top: 2px;
  font-size: 10px;
  font-weight: 600;
  color: var(--mute);
  opacity: 0.8;
}

.pg-cname {
  font-weight: 650;
  color: var(--ink);
}

.pg-cn {
  display: block;
}

.pg-cd {
  display: block;
  font-size: var(--fs-caption2);
  font-weight: 500;
  color: var(--mute);
}

.pg-cellbtn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 2px 8px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: none;
  font: inherit;
  color: var(--body);
  cursor: pointer;
}

a.pg-cellbtn:hover,
button.pg-cellbtn:hover:not(:disabled) {
  border-color: var(--tint-line);
  background: var(--fill);
}

.pg-cellbtn:focus-visible {
  outline: 3px solid var(--indigo);
  outline-offset: 1px;
}

.pg-cellnew {
  color: var(--indigo);
  font-weight: 700;
  font-size: var(--fs-subhead);
  line-height: 1;
}

.pg-celloff {
  color: var(--mute);
  cursor: default;
}

.pg-engines {
  display: inline-flex;
  gap: 4px;
}

.pg-eng {
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 6px;
  border-radius: 100px;
  background: var(--tint);
  color: var(--indigo);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.pg-sharecol {
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: var(--ink);
  font-weight: 650;
}

.pg-foot,
.pg-legend {
  margin: 10px 0 0;
  font-size: var(--fs-caption2);
  color: var(--mute);
}

.pg-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 0;
  list-style: none;
}

.pg-legend li {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.pg-err {
  margin: 8px 0 0;
  font-size: var(--fs-caption);
  color: var(--verm);
}
</style>
